import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Plus, FileText, Calendar, Euro, Building2, 
  ChevronRight, AlertCircle, Loader2 
} from "lucide-react";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { CreateTenderDialog } from "@/components/tenders/CreateTenderDialog";
import { toast } from "sonner";
import { B2BModuleGate, ModuleLimitBanner } from "@/components/b2b";

type TenderStatus = 
  | "draft" 
  | "analyzing" 
  | "go" 
  | "no_go" 
  | "in_progress" 
  | "submitted" 
  | "won" 
  | "lost" 
  | "cancelled";

interface Tender {
  id: string;
  title: string;
  buyer_name: string | null;
  deadline: string | null;
  budget: number | null;
  status: TenderStatus;
  summary: string | null;
  created_at: string;
}

const STATUS_CONFIG: Record<TenderStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
  draft: { label: "Brouillon", variant: "secondary" },
  analyzing: { label: "Analyse IA", variant: "outline", className: "border-amber-500 text-amber-600 bg-amber-50" },
  go: { label: "Go", variant: "default", className: "bg-emerald-500 hover:bg-emerald-600" },
  no_go: { label: "No Go", variant: "destructive" },
  in_progress: { label: "En cours", variant: "default", className: "bg-blue-500 hover:bg-blue-600" },
  submitted: { label: "Soumis", variant: "outline", className: "border-purple-500 text-purple-600 bg-purple-50" },
  won: { label: "Gagné 🎉", variant: "default", className: "bg-emerald-600 hover:bg-emerald-700" },
  lost: { label: "Perdu", variant: "secondary", className: "bg-muted text-muted-foreground" },
  cancelled: { label: "Annulé", variant: "secondary" },
};

export default function Tenders() {
  const navigate = useNavigate();
  const { user, status, isHydrated } = useAuth();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Wait for auth to be hydrated
  if (status === "loading" || status === "hydrating" || !isHydrated) {
    return (
      <div className="container max-w-5xl mx-auto p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { data: tenders, isLoading, error } = useQuery({
    queryKey: ["tenders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenders")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Tender[];
    },
    enabled: !!user,
  });

  const createTender = useMutation({
    mutationFn: async (values: { title: string; deadline?: string; budget?: number; buyer_name?: string }) => {
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("tenders")
        .insert({
          user_id: user.id,
          title: values.title,
          deadline: values.deadline || null,
          budget: values.budget || null,
          buyer_name: values.buyer_name || null,
          status: "draft" as TenderStatus,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tenders"] });
      setShowCreateDialog(false);
      toast.success("Projet créé avec succès");
      navigate(`/tenders/${data.id}`);
    },
    onError: (error) => {
      console.error("Error creating tender:", error);
      toast.error("Erreur lors de la création du projet");
    },
  });

  const getDeadlineInfo = (deadline: string | null) => {
    if (!deadline) return null;
    
    const deadlineDate = new Date(deadline);
    const daysLeft = differenceInDays(deadlineDate, new Date());
    const isOverdue = isPast(deadlineDate);

    return { deadlineDate, daysLeft, isOverdue };
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6">
        <AlertCircle className="w-12 h-12 text-destructive" />
        <p className="text-muted-foreground">Erreur lors du chargement des projets</p>
        <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ["tenders"] })}>
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <B2BModuleGate module="tenders">
    <div className="container max-w-5xl mx-auto p-4 md:p-6 space-y-6">
      {/* Usage Banner */}
      <ModuleLimitBanner module="tenders" />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Appels d'Offres</h1>
          <p className="text-muted-foreground text-sm">
            Gérez vos réponses aux marchés publics
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Nouveau Projet
        </Button>
      </div>

      {/* Tender List */}
      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            </Card>
          ))}
        </div>
      ) : tenders && tenders.length > 0 ? (
        <div className="grid gap-3">
          {tenders.map((tender) => {
            const deadlineInfo = getDeadlineInfo(tender.deadline);
            const statusConfig = STATUS_CONFIG[tender.status];

            return (
              <Card
                key={tender.id}
                className="p-4 hover:bg-muted/30 transition-colors cursor-pointer group"
                onClick={() => navigate(`/tenders/${tender.id}`)}
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{tender.title}</h3>
                      <Badge 
                        variant={statusConfig.variant}
                        className={cn("shrink-0", statusConfig.className)}
                      >
                        {statusConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                      {tender.buyer_name && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {tender.buyer_name}
                        </span>
                      )}
                      
                      {deadlineInfo && (
                        <span className={cn(
                          "flex items-center gap-1",
                          deadlineInfo.isOverdue && "text-destructive",
                          !deadlineInfo.isOverdue && deadlineInfo.daysLeft <= 7 && "text-amber-600"
                        )}>
                          <Calendar className="w-3.5 h-3.5" />
                          {format(deadlineInfo.deadlineDate, "d MMM yyyy", { locale: fr })}
                          {!deadlineInfo.isOverdue && deadlineInfo.daysLeft <= 14 && (
                            <span className="text-xs">
                              ({deadlineInfo.daysLeft}j)
                            </span>
                          )}
                        </span>
                      )}

                      {tender.budget && (
                        <span className="flex items-center gap-1">
                          <Euro className="w-3.5 h-3.5" />
                          {new Intl.NumberFormat("fr-FR", { 
                            notation: tender.budget >= 1000000 ? "compact" : "standard",
                            maximumFractionDigits: 0 
                          }).format(tender.budget)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-foreground transition-colors shrink-0" />
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Aucun projet d'appel d'offres</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Créez votre premier projet pour commencer à gérer vos réponses
              </p>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2 mt-2">
              <Plus className="w-4 h-4" />
              Créer un projet
            </Button>
          </div>
        </Card>
      )}

      {/* Create Dialog */}
      <CreateTenderDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={(values) => createTender.mutate(values)}
        isLoading={createTender.isPending}
      />
    </div>
    </B2BModuleGate>
  );
}
